import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, Download, Share } from 'lucide-react'
import { useInstallPrompt } from '../api/install'
import {
  currentSubscription,
  fetchPushCategories,
  fetchPushConfig,
  isIos,
  obstacleFor,
  setPushCategory,
  subscribeThisDevice,
  unsubscribeThisDevice,
  type PushObstacle,
} from '../api/push'

/**
 * Notifications on this phone (D-530).
 *
 * **Per device, and the wording has to make that obvious.** A parent with a
 * phone and an iPad has two subscriptions, so "you have notifications on" is a
 * lie on the device she is holding if she switched them on somewhere else. Every
 * string here says *this device*.
 *
 * **Four ways it cannot be switched on, and each needs a different screen.**
 * "Add to your Home Screen first" is an instruction and the iOS case; "your
 * browser cannot do this" is an apology; "you said no" has to point at browser
 * settings, which is the one thing the page cannot fix; and "the studio is not
 * sending these yet" is nobody's fault at all. A single disabled switch for all
 * four would be a control that appears broken.
 *
 * **Not on a classroom tablet.** The endpoint refuses a kiosk session (D-233,
 * D-529); this refuses to render, so the tablet has no control that fails.
 */
export default function PushNotificationsView({ isKiosk }: { isKiosk: boolean }) {
  const [subscribed, setSubscribed] = useState<boolean | null>(null)
  // Above the kiosk early return: a hook after one is called conditionally,
  // which React counts and a wall tablet would have crashed on.
  const { canInstall, install } = useInstallPrompt()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: config } = useQuery({
    queryKey: ['push-config'],
    queryFn: fetchPushConfig,
    staleTime: Infinity,
    enabled: !isKiosk,
  })

  /*
   * **The browser is the source of truth for this device, not the server.**
   * The server knows every device on the account; only the browser knows
   * whether *this* one holds a subscription. Reading the server's list would
   * show the switch on because her phone is on, while she is looking at her
   * laptop.
   */
  useEffect(() => {
    if (isKiosk) return

    let alive = true

    currentSubscription()
      .then((s) => alive && setSubscribed(s !== null))
      .catch(() => alive && setSubscribed(false))

    return () => {
      alive = false
    }
  }, [isKiosk])

  if (isKiosk) return null

  const obstacle = obstacleFor(config)

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Notifications on this device
      </h2>

      <p className="mt-1 text-[12.5px] text-ink-3">
        A closure, a class cancelled, a private lesson answered &mdash; the things worth knowing
        before you set off. You will still see everything in the app.
      </p>

      {/*
        <b>Installing on purpose</b> (D-645).

        Chrome offers this once, after its own engagement heuristic, and a
        single dismissal suppresses it for months with no way to ask for it
        again - which is how one tester saw the prompt and another never did.
        Holding the invitation ourselves means she can take it up whenever she
        looks, and on Android an installed app is also what makes notifications
        possible at all.

        Absent on iOS, where the browser fires no such event and the written
        Share-button steps below are the only route.
      */}
      {canInstall && (
        <button
          type="button"
          onClick={() => void install()}
          className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-accent px-3 py-2.5 text-[13.5px] font-semibold text-accent-2"
        >
          <Download size={15} strokeWidth={2.5} aria-hidden="true" />
          Install this app on your phone
        </button>
      )}

      {obstacle ? (
        <Blocked obstacle={obstacle} />
      ) : (
        <>
          {/*
            **On is a state, and it needs saying rather than implying** (D-892).

            This panel already changed when the device was on - the verb flipped
            to *Turn off*, a sentence appeared, the categories unfolded - but
            nothing on it said *on*. The strongest signal was a button offering
            to undo something, in the same grey box as the off state, so the two
            screens were told apart by reading one word carefully.

            Honest because it is the browser's answer about the browser, which
            is the one thing this screen does know (D-530). What the browser
            cannot see is whether the studio still holds a row for it - hence
            `reassertThisDevice`, which makes the claim true rather than louder.
          */}
          {subscribed && !busy && (
            <>
              <p
                role="status"
                className="mt-3 flex items-center gap-2 rounded-xl border border-good bg-good-bg px-3.5 py-2.5 text-[13.5px] font-semibold text-good"
              >
                <BellRing size={15} strokeWidth={2.5} aria-hidden="true" />
                On for this device
              </p>

              <p className="mt-1.5 text-[12.5px] text-ink-3">
                Your other devices are set separately.
              </p>
            </>
          )}

          <button
            type="button"
            disabled={busy || subscribed === null}
            onClick={async () => {
              setBusy(true)
              setError(null)

              try {
                if (subscribed) {
                  await unsubscribeThisDevice()
                  setSubscribed(false)
                } else {
                  await subscribeThisDevice(config!.publicKey!)
                  setSubscribed(true)
                }
              } catch (e) {
                setError((e as Error).message)
              } finally {
                setBusy(false)
              }
            }}
            /*
              **Demoted once it is on, because the status above is the message
              now** (D-892). Turning them off is the rarer act, and a bordered
              button of equal weight beside a state that says *on* reads as two
              competing claims. It keeps `min-h-[44px]`: quieter is a matter of
              border and ink, never of a target a thumb then misses.
            */
            className={
              subscribed && !busy
                ? 'mt-2 flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 py-2 text-[12.5px] font-semibold text-ink-3 hover:text-accent-2 disabled:opacity-60'
                : 'mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-line-2 px-4 py-2.5 text-[13.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2 disabled:opacity-60'
            }
          >
            {busy ? (
              'Just a moment...'
            ) : subscribed ? (
              // Still naming the device (D-530): a parent with a phone and an
              // iPad reads this one string without the one above it.
              'Turn off on this device'
            ) : (
              <>
                <BellRing size={15} strokeWidth={2.5} aria-hidden="true" />
                Turn on for this device
              </>
            )}
          </button>

          {subscribed && !busy && (
            <>
              {/*
                **Only once this device is on** (D-532). The categories are
                per-account and apply to every device — but offering them to
                somebody who has not switched anything on is four controls that
                cannot do anything yet, above the one control that can.
              */}
              <Categories />
            </>
          )}
        </>
      )}

      {error && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}
    </section>
  )
}

/**
 * Which kinds she wants, across all her devices (D-532).
 *
 * **Per account, not per device**, and the wording has to say so — the switch
 * above is about this phone, these are about her. Setting them separately per
 * device would mean answering the same four questions on every phone she owns.
 *
 * **A category she would never receive is not listed at all.** The server
 * decides that: a family has no use for a Cover switch, because no cover request
 * is ever addressed to her, and a control that cannot change anything makes the
 * others look untrustworthy.
 */
function Categories() {
  const queryClient = useQueryClient()

  const { data: rows, isPending, isError } = useQuery({
    queryKey: ['push-categories'],
    queryFn: fetchPushCategories,
  })

  const save = useMutation({
    mutationFn: ({ category, enabled }: { category: string; enabled: boolean }) =>
      setPushCategory(category, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['push-categories'] }),
  })

  if (isPending) return null

  /*
   * **A failed load says so** (D-209). Every database-backed endpoint refuses
   * for about 75 seconds after a deploy (D-33) and `retry: 1` gives up in about
   * a second, so the ordinary post-deploy case is this list failing — and
   * rendering nothing would tell her she has no settings rather than that we
   * could not read them.
   */
  if (isError) {
    return (
      <p className="mt-3 text-[12.5px] text-ink-3">
        Could not load which kinds you have switched on. This device is still set up.
      </p>
    )
  }

  if (!rows?.length) return null

  return (
    <fieldset className="mt-3 border-0 p-0">
      <legend className="mb-1.5 text-[12px] font-semibold text-ink-3">
        What to buzz about, on all your devices
      </legend>

      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.category}>
            <label className="flex items-start gap-2.5 text-[13px]">
              <input
                type="checkbox"
                checked={row.enabled}
                disabled={save.isPending}
                onChange={(e) =>
                  save.mutate({ category: row.category, enabled: e.target.checked })
                }
                className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
              />

              <span className="min-w-0">
                <span className="block font-semibold">
                  {row.name}

                  {/*
                    **"On because nobody said otherwise" is not the same as "on
                    because I chose it"** (D-532), and a panel of ticks cannot
                    tell them apart. Marking the untouched ones is what lets
                    somebody see at a glance whether they have ever set this up
                    — and it disappears the moment they do.
                  */}
                  {row.isDefault && (
                    <span className="ml-1.5 align-middle text-[10.5px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
                      Studio default
                    </span>
                  )}
                </span>
                <span className="block text-[12px] text-ink-3">{row.describes}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      {save.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          That did not save. Try again in a moment.
        </p>
      )}
    </fieldset>
  )
}

/**
 * Why it cannot be switched on here, said in a way somebody can act on.
 *
 * **The iOS one is an instruction, and it is the only one worth illustrating.**
 * Apple grants Web Push to an installed app and never to a Safari tab, so the
 * answer is a sequence of taps rather than an apology — and a parent who is told
 * "your browser does not support this" will simply believe it.
 */
function Blocked({ obstacle }: { obstacle: PushObstacle }) {
  if (obstacle === 'needs-installing') {
    return (
      <div className="mt-3 rounded-xl border border-line-2 bg-surface-2 px-3.5 py-3">
        <p className="text-[12.5px] font-semibold text-ink-2">
          Add this to your Home Screen first
        </p>

        <ol className="mt-1.5 space-y-1 text-[12.5px] text-ink-3">
          <li className="flex items-start gap-1.5">
            <Share size={14} strokeWidth={2} className="mt-0.5 shrink-0" />
            <span>Tap the Share button at the bottom of Safari.</span>
          </li>
          <li>Choose &ldquo;Add to Home Screen&rdquo;.</li>
          <li>Open the app from your Home Screen and come back here.</li>
        </ol>

        <p className="mt-2 text-[11.5px] text-ink-3">
          {/* Said plainly rather than left as a mystery: a parent who thinks the
              app is broken will stop trying. */}
          iPhones only allow notifications for apps added this way.
        </p>
      </div>
    )
  }

  const wording: Record<Exclude<PushObstacle, 'needs-installing'>, string> = {
    blocked:
      'Notifications are blocked for this site. You can turn them back on in your browser settings, then come back here.',
    unsupported: isIos()
      ? 'This iPhone is running a version of iOS that cannot do notifications yet. Everything still appears in the app.'
      : 'This browser cannot show notifications. Everything still appears in the app.',
    'not-configured':
      'The studio has not switched these on yet. Everything appears in the app in the meantime.',
  }

  return <p className="mt-3 text-[12.5px] text-ink-3">{wording[obstacle]}</p>
}
